import type { Metadata } from "next";

import Env from "@/core/config/env";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type AppLocale } from "@/core/i18n/config";
import { getLocalizedPath } from "@/core/i18n/routing";

const DEFAULT_TITLE = "ItsHealer | Therapy, Counseling, and Guided Mental Wellness";
const DEFAULT_DESCRIPTION =
  "Book verified therapists, counselors, and guided mental wellness support for anxiety, relationships, trauma, sleep, and emotional healing.";

export const SITE_CONFIG = {
  name: "ItsHealer",
  url: Env.siteUrl,
  defaultTitle: DEFAULT_TITLE,
  defaultDescription: DEFAULT_DESCRIPTION,
  locale: DEFAULT_LOCALE,
  countryCode: "IN",
  region: "IN",
  placeName: "India",
  coordinates: {
    latitude: "20.5937",
    longitude: "78.9629",
  },
  contact: {
    email: "care@itshealer.com",
    phone: "+91 80813 01190",
  },
} as const;

export const NO_INDEX_METADATA: Metadata = {
  robots: {
    follow: false,
    index: false,
  },
};

const OPEN_GRAPH_LOCALE: Record<AppLocale, string> = {
  en: "en_IN",
  hi: "hi_IN",
};

interface PageMetadataOptions {
  description?: string;
  images?: string[];
  locale: AppLocale;
  noIndex?: boolean;
  pathname?: string;
  title: string;
  type?: "article" | "profile" | "website";
}

export function absoluteUrl(pathname = "/"): string {
  return new URL(pathname, `${SITE_CONFIG.url}/`).toString();
}

export function getLanguageAlternates(pathname = "/"): Record<string, string> {
  const languages = Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, getLocalizedPath(locale, pathname)]),
  );

  return {
    ...languages,
    "x-default": getLocalizedPath(DEFAULT_LOCALE, pathname),
  };
}

export function createPageMetadata({
  description = SITE_CONFIG.defaultDescription,
  images,
  locale,
  noIndex = false,
  pathname = "/",
  title,
  type = "website",
}: PageMetadataOptions): Metadata {
  const canonical = getLocalizedPath(locale, pathname);
  const alternateLocales = SUPPORTED_LOCALES.filter(
    (supportedLocale) => supportedLocale !== locale,
  ).map((supportedLocale) => OPEN_GRAPH_LOCALE[supportedLocale]);

  return {
    alternates: {
      canonical,
      languages: getLanguageAlternates(pathname),
    },
    description,
    openGraph: {
      alternateLocale: alternateLocales,
      description,
      images,
      locale: OPEN_GRAPH_LOCALE[locale],
      siteName: SITE_CONFIG.name,
      title,
      type,
      url: canonical,
    },
    robots: noIndex
      ? {
          follow: false,
          index: false,
        }
      : undefined,
    title,
    twitter: {
      card: images?.length ? "summary_large_image" : "summary",
      description,
      images,
      title,
    },
  };
}

export function getGlobalMetadata(): Metadata {
  return {
    applicationName: SITE_CONFIG.name,
    authors: [{ name: SITE_CONFIG.name, url: SITE_CONFIG.url }],
    category: "mental health",
    creator: SITE_CONFIG.name,
    description: SITE_CONFIG.defaultDescription,
    formatDetection: {
      address: false,
      email: false,
      telephone: false,
    },
    keywords: [
      "online therapy India",
      "mental wellness",
      "counseling",
      "therapists",
      "relationship therapy",
      "anxiety support",
      "trauma recovery",
      "guided meditation",
    ],
    metadataBase: new URL(SITE_CONFIG.url),
    openGraph: {
      countryName: SITE_CONFIG.placeName,
      description: SITE_CONFIG.defaultDescription,
      locale: OPEN_GRAPH_LOCALE[SITE_CONFIG.locale],
      siteName: SITE_CONFIG.name,
      title: SITE_CONFIG.defaultTitle,
      type: "website",
      url: getLocalizedPath(SITE_CONFIG.locale, "/"),
    },
    publisher: SITE_CONFIG.name,
    robots: {
      follow: true,
      googleBot: {
        follow: true,
        index: true,
        "max-image-preview": "large",
        "max-snippet": -1,
        "max-video-preview": -1,
      },
      index: true,
    },
    title: {
      default: SITE_CONFIG.defaultTitle,
      template: `%s | ${SITE_CONFIG.name}`,
    },
    twitter: {
      card: "summary_large_image",
      description: SITE_CONFIG.defaultDescription,
      title: SITE_CONFIG.defaultTitle,
    },
    other: {
      "DC.title": SITE_CONFIG.name,
      distribution: "global",
      "geo.placename": SITE_CONFIG.placeName,
      "geo.position": `${SITE_CONFIG.coordinates.latitude};${SITE_CONFIG.coordinates.longitude}`,
      "geo.region": SITE_CONFIG.region,
      ICBM: `${SITE_CONFIG.coordinates.latitude}, ${SITE_CONFIG.coordinates.longitude}`,
      rating: "general",
    },
  };
}

export function getOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    areaServed: {
      "@type": "Country",
      name: SITE_CONFIG.placeName,
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: SITE_CONFIG.contact.email,
      telephone: SITE_CONFIG.contact.phone,
    },
    description: SITE_CONFIG.defaultDescription,
    email: SITE_CONFIG.contact.email,
    name: SITE_CONFIG.name,
    telephone: SITE_CONFIG.contact.phone,
    url: SITE_CONFIG.url,
  };
}

export function getWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    inLanguage: SUPPORTED_LOCALES,
    name: SITE_CONFIG.name,
    potentialAction: {
      "@type": "SearchAction",
      queryInput: "required name=search_term_string",
      target: absoluteUrl("/en/search?q={search_term_string}"),
    },
    url: SITE_CONFIG.url,
  };
}
